import { api, type SearchHit } from './api';
import { esc, html } from './ui';

/**
 * The search box.
 *
 * Debounced, keyboard-navigable, and closed by Escape or a click outside. It
 * searches on two characters because registrations are short and a CAMO office
 * types "BUC" more often than a whole word.
 *
 * Results are grouped by kind rather than blended: someone searching a part
 * number wants the component, and putting a document with a similar title
 * above it because it scored better would be a worse answer, not a smarter one.
 */

const KIND_LABEL: Record<SearchHit['kind'], string> = {
  AIRCRAFT: 'Aircraft',
  DEFECT: 'Defect',
  WORK_PACK: 'Work pack',
  COMPONENT: 'Component',
  DIRECTIVE: 'AD / SB',
  DOCUMENT: 'Document',
  PERSON: 'Person',
  INVOICE: 'Invoice',
};

const ORDER: SearchHit['kind'][] = [
  'AIRCRAFT', 'DEFECT', 'WORK_PACK', 'COMPONENT', 'DIRECTIVE', 'DOCUMENT', 'PERSON', 'INVOICE',
];

function render(hits: SearchHit[], query: string, active: number): string {
  if (!hits.length) {
    return html`<div class="search-empty">Nothing matching “${esc(query)}”.</div>`;
  }

  let index = -1;
  return ORDER.filter((k) => hits.some((h) => h.kind === k))
    .map((kind) => {
      const rows = hits
        .filter((h) => h.kind === kind)
        .map((h) => {
          index += 1;
          return html`<button class="search-hit${index === active ? ' on' : ''}" data-i="${index}" data-href="${esc(h.href)}">
            <span class="sh-l">${esc(h.label)}</span>
            <span class="sh-d">${esc(h.detail)}</span>
          </button>`;
        })
        .join('');
      return html`<div class="search-group"><div class="lbl">${esc(KIND_LABEL[kind])}</div>${rows}</div>`;
    })
    .join('');
}

export function bindSearch(root: HTMLElement, navigate: (path: string) => void): void {
  const input = root.querySelector<HTMLInputElement>('input[type="search"]');
  if (!input) return;

  const panel = document.createElement('div');
  panel.className = 'search-panel';
  panel.hidden = true;
  input.parentElement?.appendChild(panel);

  let hits: SearchHit[] = [];
  let active = 0;
  let timer: number | undefined;
  let seq = 0;

  const close = () => {
    panel.hidden = true;
    active = 0;
  };

  const paint = () => {
    panel.innerHTML = render(hits, input.value, active);
    panel.hidden = false;
    panel.querySelectorAll<HTMLElement>('[data-href]').forEach((b) =>
      b.addEventListener('mousedown', (e) => {
        // mousedown, not click: blur would close the panel first.
        e.preventDefault();
        close();
        input.value = '';
        navigate(b.dataset.href!);
      }),
    );
  };

  const run = async () => {
    const q = input.value.trim();
    if (q.length < 2) {
      close();
      return;
    }
    // Only the newest response is allowed to paint; a slow earlier one that
    // arrives late would otherwise overwrite the results for what was typed.
    const mine = ++seq;
    try {
      const r = await api.search(q);
      if (mine !== seq) return;
      hits = r.hits;
      active = 0;
      paint();
    } catch {
      if (mine === seq) close();
    }
  };

  input.addEventListener('input', () => {
    window.clearTimeout(timer);
    timer = window.setTimeout(run, 180);
  });

  input.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      close();
      input.blur();
      return;
    }
    if (panel.hidden || !hits.length) return;

    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      active = (active + (e.key === 'ArrowDown' ? 1 : hits.length - 1)) % hits.length;
      paint();
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const hit = hits[active];
      if (hit) {
        close();
        input.value = '';
        navigate(hit.href);
      }
    }
  });

  input.addEventListener('focus', () => {
    if (input.value.trim().length >= 2 && hits.length) paint();
  });

  document.addEventListener('click', (e) => {
    if (!panel.contains(e.target as Node) && e.target !== input) close();
  });
}
